import { Backdrop, Box, Button, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { Dispatch } from "redux";
import { useEffect, useState } from "react";

import {
	setLoggedUser,
	setIsAuthenticated,
	setIsPatientsEnabled,
	setPageState,
	setHeaderState,
} from "../../store/reducer";
import ConfirmInformationModal from "../../widgets/ConfirmInformationModal/ConfirmInformationModal";
import { formatTimeWithoutSeconds } from "../../utils/stringToDate";

// ASSETS
import { ReactComponent as MicrosoftIcon } from "../../assets/icons/login/microsoft_logo.svg";
import { ReactComponent as NurasignLogo } from "../../assets/icons/login/logo_nurasign.svg";
import MaintenanceBackground from "../../assets/images/maintenance_background.png";
import { ReactComponent as Icon01 } from "../../assets/icons/login/icon_01.svg";
import { ReactComponent as Icon02 } from "../../assets/icons/login/icon_02.svg";
import { ReactComponent as Icon03 } from "../../assets/icons/login/icon_03.svg";
import { ReactComponent as Icon04 } from "../../assets/icons/login/icon_04.svg";
import { ReactComponent as Icon05 } from "../../assets/icons/login/icon_05.svg";
import { ReactComponent as Icon06 } from "../../assets/icons/login/icon_06.svg";
import { ReactComponent as Icon07 } from "../../assets/icons/login/icon_07.svg";
import { ReactComponent as Icon08 } from "../../assets/icons/login/icon_08.svg";
import { ReactComponent as Icon09 } from "../../assets/icons/login/icon_09.svg";
import { ReactComponent as Icon10 } from "../../assets/icons/login/icon_10.svg";
import { ReactComponent as Icon11 } from "../../assets/icons/login/icon_11.svg";
import { ReactComponent as Icon12 } from "../../assets/icons/login/icon_12.svg";
import { ReactComponent as Icon13 } from "../../assets/icons/login/icon_13.svg";
import { ReactComponent as Icons } from "../../assets/icons/login/icons.svg";
import Logo from "../../assets/images/logo_base_muguerza.png";
import User01 from "../../assets/images/login/user_01.png";
import User02 from "../../assets/images/login/user_02.png";
import User03 from "../../assets/images/login/user_03.png";
import User04 from "../../assets/images/login/user_04.png";
import User05 from "../../assets/images/login/user_05.png";
import User06 from "../../assets/images/login/user_06.png";
import User07 from "../../assets/images/login/user_07.png";
import Grain from "../../assets/images/grain_login.png";

import { HeaderState } from "../../components/Header/HeaderUtils";
import { URI, URI_API, getHeaders } from "../../utils/config";
import { LoggedUser, Schedule } from "../../store/interfaces";
import { LettersPullUp } from "./LettersPullUp";
import { PageState } from "../Home/HomeUtils";
import Levitator from "./Levitator";
import {
	FLOORS_MAP,
	HOSPITALS_AVAILABLE,
	HOSPITALS_MAP,
	Position,
	POSITIONS_AVAILABLE,
} from "./LoginUtils";
import "./Login.scss";

export default function Login() {
	const { instance } = useMsal();

	const [maintenanceIsActive, setMaintenanceIsActive] = useState<boolean | null>(null);
	const [userName, setUserName] = useState("");
	const [userHospital, setUserHospital] = useState("");
	const [inProgress, setInProgress] = useState<boolean>(false);
	const [completeInformationModalOpen, setCompleteInformationModalOpen] = useState(false);
	const [resolvePromise, setResolvePromise] = useState<
		| ((value: {
				nominaNumber: string;
				classX: string;
				vitalSupport: string;
				nominaExists: boolean;
		  }) => void)
		| null
	>(null);

	const dispatch: Dispatch<any> = useDispatch();
	let navigator = useNavigate();

	const handleOpenCompleteInformationModal = (): Promise<{
		nominaNumber: string;
		classX: string;
		vitalSupport: string;
		nominaExists: boolean;
	}> => {
		return new Promise((resolve) => {
			setResolvePromise(() => resolve);
			setCompleteInformationModalOpen(true);
		});
	};

	const handleContinue = (
		nominaNumber: string,
		classX: string,
		vitalSupport: string,
		nominaExists: boolean,
	) => {
		if (resolvePromise) {
			resolvePromise({ nominaNumber, classX, vitalSupport, nominaExists });
		}
		setCompleteInformationModalOpen(false);
	};

	const handleCloseModal = () => {
		setCompleteInformationModalOpen(false);
	};

	/**
	 * Obtiene los hospitales y pisos asignados al usuario a partir de los grupos de Active Directory.
	 * @param accessToken string
	 * @returns { userHospitals: string[], userFloors: string[] }
	 */
	const getUserHospitalsAndFloorsFromMicrosoft = async (
		accessToken: string,
	): Promise<{ userHospitals: string[]; userFloors: string[]; userPosition: Position }> => {
		// Obtenemos los grupos
		const res = await fetch(
			"https://graph.microsoft.com/v1.0/me/transitiveMemberOf/microsoft.graph.group?$count=true",
			{
				headers: {
					Authorization: "Bearer " + accessToken,
				},
			},
		).then((data) => data.json());

		const ad_groups: any[] = [];
		res.value.forEach((group: any) => {
			ad_groups.push(group.displayName);
		});

		// Obtenemos el Tipo de Usuario, los hospitales y los pisos de los Grupos AD
		const assignedPositions = ad_groups.filter((group) =>
			POSITIONS_AVAILABLE.some((position: string) => group.includes(position)),
		);
		const userPosition: Position = assignedPositions[0];

		const assignedHospitals = ad_groups.filter((group) =>
			HOSPITALS_AVAILABLE.some((hospital: string) => group.includes(hospital)),
		);

		const assignedFloors = ad_groups.filter((group) => group.includes("nurap"));

		let userHospitals: string[] = [];
		let userFloors: string[] = [];

		// Si es superusuario, asignar todas las áreas de todos los hospitales
		if (userPosition === Position.SUPER) {
			userHospitals = HOSPITALS_AVAILABLE.map(
				(hospital: string) => HOSPITALS_MAP[hospital] || hospital,
			);

			userFloors = Object.values(FLOORS_MAP);
		} else {
			// Si no es superusuario, tomar los permisos de los grupos del active directory
			userHospitals = assignedHospitals.map(
				(hospital: string) => HOSPITALS_MAP[hospital] || hospital,
			);

			userFloors = assignedFloors.map((floor: string) => FLOORS_MAP[floor] || floor);
		}

		return { userHospitals, userFloors, userPosition };
	};

	/**
	 * Verifica si un usuario ya existe en el sistema utilizando los claims del ID token y el access token.
	 *
	 * - Obtiene los hospitales, pisos y posición asociados al usuario desde Microsoft.
	 * - Verifica si el usuario existe en el backend mediante el endpoint `/user_exists`.
	 * - Si el usuario no existe (HTTP 204), solicita información adicional y crea el usuario
	 *   usando el endpoint `/create_user_nurse` o `/create_user`, según el rol del usuario.
	 * - Establece el nombre del usuario en el estado.
	 * - Obtiene y almacena un token para la sesión del usuario.
	 *
	 * @param idTokenClaims - Claims extraídos del ID token del usuario (por ejemplo, nombre, correo, etc.).
	 * @param accessToken - Access token para autenticar solicitudes a Microsoft y APIs del backend.
	 * @returns Un objeto que contiene los hospitales asociados y la posición del usuario.
	 */
	const verifyExistingUser = async (idTokenClaims: any, accessToken: string) => {
		const { userHospitals, userFloors, userPosition } =
			await getUserHospitalsAndFloorsFromMicrosoft(accessToken);

		const user_exists = await fetch(
			`${URI_API}/user_exists`,
			getHeaders("POST", {
				id_token_claims: idTokenClaims,
			}),
		);

		if (user_exists.status === 204) {
			setUserName(idTokenClaims.name);
			if (userHospitals.length === 1)
			setUserHospital(userHospitals[0]);
			const { nominaNumber, classX, vitalSupport, nominaExists } =
				await handleOpenCompleteInformationModal();
			if (nominaExists === false) {
				await fetch(
					`${URI_API}/create_user_nurse`,
					getHeaders("POST", {
						id_token_claims: idTokenClaims,
						nominaNumber: nominaNumber,
						classx: classX,
						vitalSupport: vitalSupport,
						hospital: userHospitals[0],
					}),
				);
			} else {
				await fetch(
					`${URI_API}/create_user`,
					getHeaders("POST", {
						id_token_claims: idTokenClaims,
						nominaNumber: nominaNumber,
						hospital: userHospitals[0],
					}),
				);
			}
		}

		await getToken(accessToken, idTokenClaims, userHospitals, userFloors);

		return { userHospitals, userPosition };
	};

	/**
	 * Recupera un token de la API utilizando la información de autenticación y contexto del usuario proporcionada.
	 *
	 * @param accessToken - El token de acceso obtenido del proveedor de autenticación.
	 * @param idTokenClaims - Los claims del ID token, que normalmente contienen información de identidad del usuario.
	 * @param userHospitals - Un arreglo con los identificadores de hospitales asociados al usuario.
	 * @param userFloors - Un arreglo con los identificadores de pisos (áreas) asociados al usuario.
	 * @returns Una promesa que se resuelve cuando la solicitud de obtención del token ha finalizado.
	 */
	const getToken = async (
		accessToken: string,
		idTokenClaims: any,
		userHospitals: string[],
		userFloors: string[],
	) => {
		await Promise.all([
			// Get Token from our API
			fetch(
				`${URI}/getAToken`,
				getHeaders("POST", {
					access_token: accessToken,
					id_token_claims: idTokenClaims,
					nura_hospital: userHospitals,
					nura_area: userFloors,
				}),
			),
		]);
	};

	const initializeSignIn = (): void => {
		setInProgress(true);
		instance
			.loginPopup()
			.then(async (res: any) => {
				const { userHospitals, userPosition } = await verifyExistingUser(
					res.idTokenClaims,
					res.accessToken,
				);

				/// Create Session in our API
				const res_user = await fetch(`${URI_API}/session_user`, getHeaders()).then(
					(res_user) => res_user.json(),
				);

				const userSchedule: Schedule = {
					name: res_user.schedule_info?.name ?? "No Disponible",
					entrance: formatTimeWithoutSeconds(res_user.schedule_info?.entrance),
					departure: formatTimeWithoutSeconds(res_user.schedule_info?.departure),
				};

				const user: LoggedUser = {
					id: res_user.id,
					fullName: res_user.display_name,
					job_title: res_user.job_title,
					email: res_user.upn,
					type: res_user.type,
					nurse_id: res_user.nurse_id,
					areas: res_user.areas,
					imageURL: "",
					hospital_id: res_user.hospital_id,
					assigned_hospitals: userHospitals,
					position: userPosition,
					schedule: userSchedule,
				};

				dispatch(setLoggedUser(user));
				dispatch(setIsAuthenticated(true));
				dispatch(setIsPatientsEnabled(false));
				dispatch(setPageState(PageState.AssistanceAdjust));
				dispatch(setHeaderState(HeaderState.Normal));
				navigator("/home");
			})
			.finally(() => {
				setInProgress(false);
			});
	};

	useEffect(() => {
		fetch(`${URI_API}/check_maintenance`)
			.then((res) => res.json())
			.then((data) => setMaintenanceIsActive(data.maintenance));
		// .then((data) => setMaintenanceIsActive(true));
	}, []);

	return (
		<Box className="login">
			<img
				src={Grain}
				className="login__grain"
			/>
			<img
				src={MaintenanceBackground}
				style={{
					display: maintenanceIsActive ? "flex" : "none",
					position: "absolute",
					height: "100vh",
					zIndex: "3",
				}}
			/>
			<Box className="login__container">
				<Box
					className="login__mosaic"
					sx={{
						display: maintenanceIsActive === true ? "none" : "flex",
					}}
				>
					<Box className="login__mosaic__row">
						<Levitator className="login__mosaic__circle login__mosaic__circle--02">
							<img src={User01} />
						</Levitator>
						<Levitator className="login__mosaic__circle">
							<Icon01 />
						</Levitator>
						<Levitator
							className="login__mosaic__circle login__mosaic__circle--big"
							canLevitate={false}
						>
							<img src={User06} />
						</Levitator>
						<Levitator className="login__mosaic__circle login__mosaic__circle--01">
							<Icon02 />
						</Levitator>
					</Box>

					<Box className="login__mosaic__row">
						<Levitator className="login__mosaic__circle login__mosaic__circle--01">
							<Icon03 />
						</Levitator>
						<Levitator className="login__mosaic__circle login__mosaic__circle--02">
							<img src={User05} />
						</Levitator>
						<Levitator className="login__mosaic__circle login__mosaic__circle--01">
							<Icon04 />
						</Levitator>
						<Levitator className="login__mosaic__circle">
							<Icon05 />
						</Levitator>
						<Levitator className="login__mosaic__circle login__mosaic__circle--02">
							<img src={User04} />
						</Levitator>
					</Box>

					<Box className="login__mosaic__row">
						<Levitator className="login__mosaic__circle">
							<Icon06 />
						</Levitator>
						<Levitator
							className="login__mosaic__circle login__mosaic__circle--big"
							canLevitate={false}
						>
							<NurasignLogo width={"17rem"} />
						</Levitator>
						<Levitator className="login__mosaic__circle login__mosaic__circle--02">
							<img src={User02} />
						</Levitator>
						<Levitator className="login__mosaic__circle login__mosaic__circle--01">
							<Icon07 />
						</Levitator>
					</Box>

					<Box className="login__mosaic__row">
						<Levitator className="login__mosaic__circle login__mosaic__circle--02">
							<img src={User03} />
						</Levitator>
						<Levitator className="login__mosaic__circle">
							<Icon08 />
						</Levitator>
						<Levitator
							className="login__mosaic__circle login__mosaic__circle--big"
							canLevitate={false}
						>
							<img src={User07} />
						</Levitator>
						<Levitator className="login__mosaic__circle">
							<Icon09 />
						</Levitator>
					</Box>
				</Box>
				<motion.div
					className="login__panel"
					initial={{ opacity: 0, y: 80 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, ease: "easeOut" }}
				>
					<Icon06 />
					<Icon02 />
					<Icon04 />
					<Icon05 />
					<Icon10 />
					<Icon11 />
					<Icon12 />
					<Icon13 />
					<Box className="login__panel__header">
						<motion.img
							src={Logo}
							initial={{ opacity: 0, y: -40 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.7, delay: 0.5 }}
						/>
						<motion.div
							initial={{ opacity: 0, y: -30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.7, delay: 1 }}
						>
							<Icons />
						</motion.div>
					</Box>
					<Box className="login__panel__footer">
						<LettersPullUp
							className="login__panel__footer__title"
							text="¡Hola de nuevo!"
						/>

						<LettersPullUp
							className="login__panel__footer__paragraph"
							text={`Conéctate a Nurasign y empieza tu turno\n  con todo en orden.`}
							pullUpDelay={0.025}
						/>

						<Button
							onClick={initializeSignIn}
							startIcon={<MicrosoftIcon />}
							disabled={maintenanceIsActive ?? true}
						>
							Iniciar sesión
						</Button>
					</Box>
				</motion.div>
			</Box>
			<Backdrop
				sx={{
					color: "#fff",
					zIndex: (theme) => theme.zIndex.drawer + 1,
				}}
				open={inProgress}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
			<ConfirmInformationModal
				showComponent={completeInformationModalOpen}
				closeComponent={handleCloseModal}
				onContinue={handleContinue}
				name={userName}
				hospital={userHospital}
			/>
		</Box>
	);
}
